'use client'
import { cn } from '@/util/cn'
import { BackspaceIcon, BookmarkIcon, SpeakerWaveIcon } from '@heroicons/react/16/solid'
import { Bars3Icon } from '@heroicons/react/24/solid'
import { useDebounce } from '@uidotdev/usehooks'
import { TargetLanguageCode } from 'deepl-node'
import { useEffect, useRef, useState } from 'react'
import CopyButton from './copy-button'

export type TooltipShow = 'clear' | 'copy' | 'bookmark' | 'reorder' | 'reorder-2' | 'audio'

type Props = {
    targetLanguage: TargetLanguageCode
    focusedLang?: TargetLanguageCode
    setFocusedLang: (lang?: TargetLanguageCode) => void
    index: number
    textValue: string
    isFetching: boolean
    tooltip?: TooltipShow
}

const DemoInput = ({ targetLanguage, focusedLang, setFocusedLang, index, tooltip, textValue }: Props) => {
    const backspaceRef = useRef(false)
    const focusedLangRef = useRef(focusedLang)
    focusedLangRef.current = focusedLang
    const textRef = useRef<HTMLSpanElement>(null)
    const isFocused = targetLanguage === focusedLang
    const [menuInteracted, setMenuInteracted] = useState(false)
    const [keepMenuOpen] = useState(false)
    const debouncedFocus = useDebounce(isFocused, menuInteracted ? 2000 : 100)
    useEffect(() => {
        if (!debouncedFocus) {
            setMenuInteracted(false)
        }
    }, [debouncedFocus, setMenuInteracted])
    const showMenu = keepMenuOpen || isFocused ? true : debouncedFocus
    const textColor = isFocused ? 'black' : `var(--skeleton-text-color-${index})`
    useEffect(() => {
        textRef.current!.innerHTML = textValue + (isFocused ? '<span class="blink-cursor">|</span>' : '')
        if (isFocused) {
            textRef.current?.focus()
            goToEnd(textRef.current!)
        }
    }, [textValue, isFocused])
    const blurTimeoutRef = useRef<NodeJS.Timeout>(null)
    return (
        <div
            className="relative transition-opacity duration-700 ease-out"
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
            }}
        >
            <span
                style={{
                    color: textColor,
                    backgroundColor: isFocused ? 'white' : `var(--skeleton-color-${index})`,
                    display: 'inline',
                    boxDecorationBreak: 'clone',
                    WebkitBoxDecorationBreak: 'clone',
                }}
                aria-readonly
                data-testid={targetLanguage}
                ref={textRef}
                lang={targetLanguage}
                className={`outline-hidden pointer-events-none inline-block p-1 text-lg transition-all ease-in-out md:text-xl`}
                spellCheck={false}
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                }}
                onFocus={() => {
                    goToEnd(textRef.current!)
                }}
                onBlur={() => {
                    clearTimeout(blurTimeoutRef.current!)
                    blurTimeoutRef.current = setTimeout(() => {
                        if (backspaceRef.current) {
                            setTimeout(() => {
                                backspaceRef.current = false
                            }, 0)
                            // we've blurred and the language hasn't changed we're unfocused
                        } else if (focusedLangRef.current === targetLanguage) {
                            setFocusedLang(undefined)
                        }
                    }, 250)
                }}
            />
            <div
                onMouseDown={() => {
                    setMenuInteracted(true)
                }}
                style={{
                    opacity: showMenu ? 1 : 0,
                    gridTemplateRows: showMenu ? '1fr' : '0fr',
                    marginTop: showMenu ? '0.5rem' : 0,
                    marginBottom: showMenu ? '0.2rem' : 0,
                    transition: 'grid-template-rows 0.05s ease-out, opacity .3s ease-out',
                }}
                className="grid"
            >
                <div className={`inline-flex gap-2 ${showMenu ? '' : 'overflow-hidden'}`}>
                    <div
                        className={cn('tooltip tooltip-accent', tooltip === 'clear' && 'tooltip-open')}
                        data-tip="Quick clear"
                    >
                        <BackspaceIcon
                            className="menu-icon"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                if (textRef.current) {
                                    backspaceRef.current = true
                                    textRef.current.focus()
                                    textRef.current.innerText = ''
                                }
                            }}
                        />
                    </div>
                    <div
                        className={cn('tooltip tooltip-accent', tooltip === 'copy' && 'tooltip-open')}
                        data-tip="Copy to clipboard"
                    >
                        <CopyButton text={textRef.current?.innerText || ''} />
                    </div>
                    <div
                        className={cn('tooltip tooltip-accent', tooltip === 'bookmark' && 'tooltip-open')}
                        data-tip="Add Bookmark"
                    >
                        <BookmarkIcon className={cn(`menu-icon stroke-primary`)} onClick={() => {}} />
                    </div>
                    <div
                        className={cn('tooltip tooltip-accent', tooltip === 'audio' && 'tooltip-open')}
                        data-tip="Play Audio"
                    >
                        <SpeakerWaveIcon className={cn(`menu-icon stroke-primary`)} onClick={() => {}} />
                    </div>
                    <div
                        className={cn('tooltip tooltip-accent', tooltip === 'reorder' && 'tooltip-open')}
                        data-tip="Reorder"
                    >
                        <Bars3Icon className="menu-icon" onClick={async () => {}} />
                    </div>
                </div>
            </div>
        </div>
    )
}
export default DemoInput

function goToEnd(textRef: HTMLSpanElement) {
    const range = document.createRange() //Create a range (a range is a like the selection but invisible)
    range.selectNodeContents(textRef) //Select the entire contents of the element with the range
    range.collapse(false) //collapse the range to the end point. false means collapse to end rather than the start
    const selection = window.getSelection() //get the selection object (allows you to change selection)
    selection?.removeAllRanges() //remove any selections already made
    selection?.addRange(range)
}
